import {
    EffectType,
    CutoutProCartoonType,
    CustomilyFilterType,
    OpenAIQuality,
    OpenAIBackground,
    OpenAISize,
    CutoutProQuality,
    RemoveBgQuality,
    PicwishUpScaleFactor,
    PicwishEnhancementType,
    OpenAIModel,
    GeminiAspectRatioType,
    GeminiImageModelsType,
    GeminiImageSizeType
} from '@/typings/customilyElements/imageEffectType';
import {
    ImageEffectPreset,
    PresetCategory,
    PresetTag,
    PresetCategoryInfo,
    EffectiveCategory,
    SPECIAL_CATEGORY_VALUES
} from '@/components/DesignStudio/ImageEffectsProperties/types/presets';

export const PRESET_CATEGORIES: PresetCategoryInfo[] = [
    {
        category: PresetCategory.TOOLS,
        label: 'Tools',
        description: 'Handy tools like background removal, face cutout, and more.',
        icon: 'fa-briefcase',
        order: 1
    },
    {
        category: PresetCategory.STYLE_MODIFIERS,
        label: 'Style Modifiers',
        description: 'Quick ways to restyle images with curated visual effects.',
        icon: 'fa-wand-magic-sparkles',
        order: 2
    },
    {
        category: PresetCategory.CUSTOM_PROMPTS,
        label: 'Custom Prompts',
        description: 'Set up a custom prompt to generate the exact effect you want.',
        icon: 'fa-message-dots',
        order: 3
    }
];

export const IMAGE_EFFECT_PRESETS: ImageEffectPreset[] = [
    {
        id: 'customily-remove-bg',
        label: 'Remove Background',
        caption: "Remove the background from your shopper's image uploads. Free Customily version available.",
        category: PresetCategory.TOOLS,
        tags: [PresetTag.CUSTOMILY],
        thumbnail: require('@/assets/design-studio/image-effects/thumbnails/generic-filter-effect-remove-bg.png'),
        isFree: true,
        order: 1,
        effectConfig: {
            presetId: 'customily-remove-bg',
            type: EffectType.CUSTOMILY_REMOVE_BG
        }
    },
    {
        id: 'cutout-pro-rmbg',
        label: 'Remove Background',
        caption: 'High quality background removal with CutOut Pro',
        category: PresetCategory.TOOLS,
        tags: [PresetTag.CUTOUT_PRO],
        thumbnail: require('@/assets/design-studio/image-effects/thumbnails/generic-filter-effect-remove-bg.png'),
        isFree: false,
        order: 2,
        effectConfig: {
            presetId: 'cutout-pro-rmbg',
            type: EffectType.CUTOUT_PRO_RMBG,
            bgColor: '',
            crop: true,
            cropMargin: '0',
            quality: CutoutProQuality.AUTO
        }
    },
    {
        id: 'cutout-pro-rmbg-blur',
        label: 'Blur Background',
        caption: "Blur the background from your shopper's image uploads using CutOut.Pro.",
        category: PresetCategory.TOOLS,
        tags: [PresetTag.CUTOUT_PRO],
        thumbnail: require('@/assets/design-studio/image-effects/thumbnails/generic-filter-effect-remove-bg-blur.jpg'),
        isFree: false,
        order: 3,
        effectConfig: {
            presetId: 'cutout-pro-rmbg-blur',
            type: EffectType.CUTOUT_PRO_RMBG,
            bgColor: 'blur',
            crop: false,
            cropMargin: '0',
            quality: CutoutProQuality.AUTO
        }
    },
    {
        id: 'remove-bg-transparent',
        label: 'Remove Background',
        caption: 'High quality background removal with Remove.BG',
        category: PresetCategory.TOOLS,
        tags: [PresetTag.REMOVE_BG],
        thumbnail: require('@/assets/design-studio/image-effects/thumbnails/generic-filter-effect-remove-bg.png'),
        isFree: false,
        order: 4,
        effectConfig: {
            presetId: 'remove-bg-transparent',
            type: EffectType.REMOVE_BG,
            bgColor: '',
            crop: true,
            cropMargin: '0',
            quality: RemoveBgQuality.AUTO
        }
    },
    {
        id: 'customily-filter-effect-watercolor',
        label: 'Watercolor Effect',
        caption: "Apply a watercolor effect to your shopper's image uploads, completely free of charge.",
        category: PresetCategory.STYLE_MODIFIERS,
        tags: [PresetTag.CUSTOMILY],
        thumbnail: require('@/assets/design-studio/image-effects/thumbnails/customily-filter-effect-watercolor.jpg'),
        isFree: true,
        order: 5,
        effectConfig: {
            presetId: 'customily-filter-effect-watercolor',
            type: EffectType.CUSTOMILY_FILTER_EFFECT,
            filterType: CustomilyFilterType.aqua
        }
    },
    {
        id: 'customily-filter-effect-pencil',
        label: 'Pencil Drawing',
        caption: "Apply a pencil sketch effect to your shopper's image uploads, completely free of charge.",
        category: PresetCategory.STYLE_MODIFIERS,
        tags: [PresetTag.CUSTOMILY],
        thumbnail: require('@/assets/design-studio/image-effects/thumbnails/customily-filter-effect-pencil.webp'),
        isFree: true,
        order: 6,
        effectConfig: {
            presetId: 'customily-filter-effect-pencil',
            type: EffectType.CUSTOMILY_FILTER_EFFECT,
            filterType: CustomilyFilterType.pencil
        }
    },
    {
        id: 'customily-filter-effect-edge',
        label: 'Edge',
        caption: "Apply an edge effect to your shopper's image uploads, completely free of charge.",
        category: PresetCategory.STYLE_MODIFIERS,
        tags: [PresetTag.CUSTOMILY],
        thumbnail: require('@/assets/design-studio/image-effects/thumbnails/customily-filter-effect-edge.webp'),
        isFree: true,
        order: 9,
        effectConfig: {
            presetId: 'customily-filter-effect-edge',
            type: EffectType.CUSTOMILY_FILTER_EFFECT,
            filterType: CustomilyFilterType.edge
        }
    },
    {
        id: 'customily-filter-effect-invert',
        label: 'Invert',
        caption: "Invert the colors of your shopper's image uploads, completely free of charge.",
        category: PresetCategory.STYLE_MODIFIERS,
        tags: [PresetTag.CUSTOMILY],
        thumbnail: require('@/assets/design-studio/image-effects/thumbnails/customily-filter-effect-invert.webp'),
        isFree: true,
        order: 12,
        effectConfig: {
            presetId: 'customily-filter-effect-invert',
            type: EffectType.CUSTOMILY_FILTER_EFFECT,
            filterType: CustomilyFilterType.invert
        }
    },
    {
        id: 'customily-filter-effect-sepia',
        label: 'Sepia',
        caption: "Apply a sepia color effect to your shopper's image uploads, completely free of charge.",
        category: PresetCategory.STYLE_MODIFIERS,
        tags: [PresetTag.CUSTOMILY],
        thumbnail: require('@/assets/design-studio/image-effects/thumbnails/customily-filter-effect-sepia.webp'),
        isFree: true,
        order: 13,
        effectConfig: {
            presetId: 'customily-filter-effect-sepia',
            type: EffectType.CUSTOMILY_FILTER_EFFECT,
            filterType: CustomilyFilterType.sepia
        }
    },
    {
        id: 'customily-face-cut-out',
        label: 'Face Cut Out',
        caption: "Automatically cut out faces from your shopper's image uploads. Free Customily version available.",
        category: PresetCategory.TOOLS,
        tags: [PresetTag.CUSTOMILY],
        thumbnail: require('@/assets/design-studio/image-effects/thumbnails/generic-filter-effect-face-cut-out.png'),
        isFree: true,
        order: 16,
        effectConfig: {
            presetId: 'customily-face-cut-out',
            type: EffectType.CUSTOMILY_FACE_CUT_OUT
        }
    },
    {
        id: 'cutout-pro-face-cut',
        label: 'Face Cut Out',
        caption: 'High precision face cutout with CutOut Pro',
        category: PresetCategory.TOOLS,
        tags: [PresetTag.CUTOUT_PRO],
        thumbnail: require('@/assets/design-studio/image-effects/thumbnails/generic-filter-effect-face-cut-out.png'),
        isFree: false,
        order: 17,
        effectConfig: {
            presetId: 'cutout-pro-face-cut',
            type: EffectType.CUTOUT_PRO_FACE_CUT_OUT,
            bgColor: '',
            crop: true,
            quality: CutoutProQuality.AUTO
        }
    },
    {
        id: 'cutout-pro-cartoon-selfie',
        label: 'Avatar styles',
        caption: "Apply avatar style effects to your shopper's image uploads using CutOut.Pro.",
        category: PresetCategory.STYLE_MODIFIERS,
        tags: [PresetTag.CUTOUT_PRO],
        thumbnail: require('@/assets/design-studio/image-effects/thumbnails/cutout-pro-filter-effect-cartoon-selfie.jpg'),
        isFree: false,
        order: 18,
        effectConfig: {
            presetId: 'cutout-pro-cartoon-selfie',
            type: EffectType.CUTOUT_PRO_CARTOON_SELFIE,
            cartoonType: CutoutProCartoonType['magic-mirror-avatar']
        }
    },
    {
        id: 'openai-oil-painting',
        label: 'Oil Painting ',
        caption: "Transform your shopper's images into a Oil Painting-style masterpiece.",
        category: PresetCategory.STYLE_MODIFIERS,
        tags: [PresetTag.OPENAI],
        thumbnail: require('@/assets/design-studio/image-effects/thumbnails/default-filter-effect-oil-painting.webp'),
        isFree: false,
        order: 19,
        effectConfig: {
            presetId: 'openai-oil-painting',
            type: EffectType.OPENAI_IMAGE_EDIT,
            quality: OpenAIQuality.AUTO,
            background: OpenAIBackground.AUTO,
            size: OpenAISize.AUTO,
            prompt: 'Recreate this image in a classic oil painting style',
            model: OpenAIModel.GPT_IMAGE_1_5
        }
    },
    {
        id: 'openai-renaissance-painting',
        label: 'Renaissance Painting',
        caption: "Transform your shopper's images into a Renaissance Painting-style masterpiece.",
        category: PresetCategory.STYLE_MODIFIERS,
        tags: [PresetTag.OPENAI],
        thumbnail: require('@/assets/design-studio/image-effects/thumbnails/default-filter-effect-renaissance-painting.webp'),
        isFree: false,
        order: 20,
        effectConfig: {
            presetId: 'openai-renaissance-painting',
            type: EffectType.OPENAI_IMAGE_EDIT,
            quality: OpenAIQuality.AUTO,
            background: OpenAIBackground.AUTO,
            size: OpenAISize.AUTO,
            prompt: 'Recreate this image in a renaissance painting style',
            model: OpenAIModel.GPT_IMAGE_1_5
        }
    },
    {
        id: 'openai-whimsical-anime',
        label: 'Whimsical Anime',
        caption: "Transform your shopper's images into a Whimsical Anime-style masterpiece.",
        category: PresetCategory.STYLE_MODIFIERS,
        tags: [PresetTag.OPENAI],
        thumbnail: require('@/assets/design-studio/image-effects/thumbnails/default-filter-effect-whimsical-anime.webp'),
        isFree: false,
        order: 21,
        effectConfig: {
            presetId: 'openai-whimsical-anime',
            type: EffectType.OPENAI_IMAGE_EDIT,
            quality: OpenAIQuality.AUTO,
            background: OpenAIBackground.AUTO,
            size: OpenAISize.AUTO,
            prompt:
                'Recreate this image as a hand-painted 2D animation frame, with soft pastel colors, whimsical fantasy scenery, detailed backgrounds and gentle, expressive characters',
            model: OpenAIModel.GPT_IMAGE_1_5
        }
    },
    {
        id: 'openai-playful-cartoon',
        label: 'Playful Cartoon',
        caption: "Transform your shopper's images into a Playful Cartoon-style masterpiece.",
        category: PresetCategory.STYLE_MODIFIERS,
        tags: [PresetTag.OPENAI],
        thumbnail: require('@/assets/design-studio/image-effects/thumbnails/default-filter-effect-playful-cartoon.webp'),
        isFree: false,
        order: 22,
        effectConfig: {
            presetId: 'openai-playful-cartoon',
            type: EffectType.OPENAI_IMAGE_EDIT,
            quality: OpenAIQuality.AUTO,
            background: OpenAIBackground.AUTO,
            size: OpenAISize.AUTO,
            prompt:
                'Recreate this image as a stylized 3D animated movie still, with smooth rounded shapes, big expressive eyes, soft cinematic lighting and a bright, family-friendly color palette',
            model: OpenAIModel.GPT_IMAGE_1_5
        }
    },
    {
        id: 'openai-anime',
        label: 'Anime',
        caption: "Transform your shopper's images into a Anime-style masterpiece.",
        category: PresetCategory.STYLE_MODIFIERS,
        tags: [PresetTag.OPENAI],
        thumbnail: require('@/assets/design-studio/image-effects/thumbnails/default-filter-effect-anime.webp'),
        isFree: false,
        order: 23,
        effectConfig: {
            presetId: 'openai-anime',
            type: EffectType.OPENAI_IMAGE_EDIT,
            quality: OpenAIQuality.AUTO,
            background: OpenAIBackground.AUTO,
            size: OpenAISize.AUTO,
            prompt: 'Recreate this image in a anime style',
            model: OpenAIModel.GPT_IMAGE_1_5
        }
    },
    {
        id: 'openai-cartoon',
        label: 'Cartoon',
        caption: "Transform your shopper's images into a Cartoon-style masterpiece.",
        category: PresetCategory.STYLE_MODIFIERS,
        tags: [PresetTag.OPENAI],
        thumbnail: require('@/assets/design-studio/image-effects/thumbnails/default-filter-effect-cartoon.webp'),
        isFree: false,
        order: 24,
        effectConfig: {
            presetId: 'openai-cartoon',
            type: EffectType.OPENAI_IMAGE_EDIT,
            quality: OpenAIQuality.AUTO,
            background: OpenAIBackground.AUTO,
            size: OpenAISize.AUTO,
            prompt: 'Recreate this image in a cartoon style',
            model: OpenAIModel.GPT_IMAGE_1_5
        }
    },
    {
        id: 'openai-cyberpunk',
        label: 'Cyberpunk',
        caption: "Transform your shopper's images into a Cyberpunk-style masterpiece.",
        category: PresetCategory.STYLE_MODIFIERS,
        tags: [PresetTag.OPENAI],
        thumbnail: require('@/assets/design-studio/image-effects/thumbnails/default-filter-effect-cyberpunk.jpg'),
        isFree: false,
        order: 25,
        effectConfig: {
            presetId: 'openai-cyberpunk',
            type: EffectType.OPENAI_IMAGE_EDIT,
            quality: OpenAIQuality.AUTO,
            background: OpenAIBackground.AUTO,
            size: OpenAISize.AUTO,
            prompt:
                'Recreate this image in a blade-runner inspired cyberpunk style, respect the original pictures proportions, surreal, clean manga style line art, cyberpunk background, retro, high-tech yet decayed atmosphere, neon-soaked shadows, reflective surfaces, contrasting bright and dark areas, tattered yet futuristic clothing, Blade Runner-inspired ambiance, clean line art',
            model: OpenAIModel.GPT_IMAGE_1_5
        }
    },
    {
        id: 'openai-retro-cartoon',
        label: 'Retro Cartoon',
        caption: "Transform your shopper's images into a Retro Cartoon-style masterpiece.",
        category: PresetCategory.STYLE_MODIFIERS,
        tags: [PresetTag.OPENAI],
        thumbnail: require('@/assets/design-studio/image-effects/thumbnails/default-filter-effect-retro-cartoon.webp'),
        isFree: false,
        order: 26,
        effectConfig: {
            presetId: 'openai-retro-cartoon',
            type: EffectType.OPENAI_IMAGE_EDIT,
            quality: OpenAIQuality.AUTO,
            background: OpenAIBackground.AUTO,
            size: OpenAISize.AUTO,
            prompt:
                'Recreate this image in a vintage 2D family-friendly cartoon style, with clean bold outlines, flat pastel colors, soft paper texture and rounded, expressive characters',
            model: OpenAIModel.GPT_IMAGE_1_5
        }
    },
    {
        id: 'openai-bw-comic',
        label: 'B&W Comic',
        caption: "Transform your shopper's images into a B&W Comic-style masterpiece.",
        category: PresetCategory.STYLE_MODIFIERS,
        tags: [PresetTag.OPENAI],
        thumbnail: require('@/assets/design-studio/image-effects/thumbnails/default-filter-effect-bw-comic.jpg'),
        isFree: false,
        order: 27,
        effectConfig: {
            presetId: 'openai-bw-comic',
            type: EffectType.OPENAI_IMAGE_EDIT,
            quality: OpenAIQuality.AUTO,
            background: OpenAIBackground.AUTO,
            size: OpenAISize.AUTO,
            prompt:
                'Recreate this image as an early 1930s black-and-white cartoon, with rubber-hose style limbs, high contrast shading, light film grain and a classic retro animation look',
            model: OpenAIModel.GPT_IMAGE_1_5
        }
    },
    {
        id: 'openai-kawaii-cartoon',
        label: 'Kawaii Cartoon',
        caption: "Transform your shopper's images into a Kawaii Cartoon-style masterpiece.",
        category: PresetCategory.STYLE_MODIFIERS,
        tags: [PresetTag.OPENAI],
        thumbnail: require('@/assets/design-studio/image-effects/thumbnails/default-filter-effect-kawaii-cartoon.jpg'),
        isFree: false,
        order: 28,
        effectConfig: {
            presetId: 'openai-kawaii-cartoon',
            type: EffectType.OPENAI_IMAGE_EDIT,
            quality: OpenAIQuality.AUTO,
            background: OpenAIBackground.AUTO,
            size: OpenAISize.AUTO,
            prompt: 'Recreate this image in a kawaii cartoon style',
            model: OpenAIModel.GPT_IMAGE_1_5
        }
    },
    {
        id: 'openai-yellowtoon',
        label: 'Yellowtoon',
        caption: "Transform your shopper's images into a Yellowtoon-style masterpiece.",
        category: PresetCategory.STYLE_MODIFIERS,
        tags: [PresetTag.OPENAI],
        thumbnail: require('@/assets/design-studio/image-effects/thumbnails/default-filter-effect-yellowtoon.webp'),
        isFree: false,
        order: 29,
        effectConfig: {
            presetId: 'openai-yellowtoon',
            type: EffectType.OPENAI_IMAGE_EDIT,
            quality: OpenAIQuality.AUTO,
            background: OpenAIBackground.AUTO,
            size: OpenAISize.AUTO,
            prompt:
                'Recreate this image in a 1990s prime-time TV cartoon style, with flat bright colors, thick black outlines, simple three-quarter views, yellow-tinted skin tones, large round eyes with dot pupils, four-fingered hands and exaggerated mouth expressions',
            model: OpenAIModel.GPT_IMAGE_1_5
        }
    },
    {
        id: 'openai-pixel-art',
        label: 'Pixel Art',
        caption: "Transform your shopper's images into a Pixel Art-style masterpiece.",
        category: PresetCategory.STYLE_MODIFIERS,
        tags: [PresetTag.OPENAI],
        thumbnail: require('@/assets/design-studio/image-effects/thumbnails/default-filter-effect-pixel-art.webp'),
        isFree: false,
        order: 30,
        effectConfig: {
            presetId: 'openai-pixel-art',
            type: EffectType.OPENAI_IMAGE_EDIT,
            quality: OpenAIQuality.AUTO,
            background: OpenAIBackground.AUTO,
            size: OpenAISize.AUTO,
            prompt: 'Recreate this image in a pixel art style',
            model: OpenAIModel.GPT_IMAGE_1_5
        }
    },
    {
        id: 'openai-cute-retro-cartoon',
        label: 'Cute Retro Cartoon',
        caption: "Transform your shopper's images into a Cute Retro-style masterpiece.",
        category: PresetCategory.STYLE_MODIFIERS,
        tags: [PresetTag.OPENAI],
        thumbnail: require('@/assets/design-studio/image-effects/thumbnails/default-filter-effect-cute-retro-cartoon.webp'),
        isFree: false,
        order: 31,
        effectConfig: {
            presetId: 'openai-cute-retro-cartoon',
            type: EffectType.OPENAI_IMAGE_EDIT,
            quality: OpenAIQuality.AUTO,
            background: OpenAIBackground.AUTO,
            size: OpenAISize.AUTO,
            prompt:
                'Recreate this image in a minimalist mid-20th-century newspaper comic strip style, with thin clean line art, oversized round heads, small dot eyes, tiny noses, simple mouths, flat muted pastel colors and a cozy, nostalgic feel',
            model: OpenAIModel.GPT_IMAGE_1_5
        }
    },
    {
        id: 'openai-3d-art',
        label: '3D Art',
        caption: "Transform your shopper's images into a 3D Art-style masterpiece.",
        category: PresetCategory.STYLE_MODIFIERS,
        tags: [PresetTag.OPENAI],
        thumbnail: require('@/assets/design-studio/image-effects/thumbnails/default-filter-effect-3d-art.webp'),
        isFree: false,
        order: 32,
        effectConfig: {
            presetId: 'openai-3d-art',
            type: EffectType.OPENAI_IMAGE_EDIT,
            quality: OpenAIQuality.AUTO,
            background: OpenAIBackground.AUTO,
            size: OpenAISize.AUTO,
            prompt: 'Recreate this image in a 3d art style ',
            model: OpenAIModel.GPT_IMAGE_1_5
        }
    },
    {
        id: 'openai-enhance',
        label: 'Enhance Photo',
        caption: "Enhance your customer's image uploads using your AI of choice.",
        category: PresetCategory.TOOLS,
        tags: [PresetTag.OPENAI],
        thumbnail: require('@/assets/design-studio/image-effects/thumbnails/default-filter-effect-enhance.webp'),
        isFree: false,
        order: 33,
        effectConfig: {
            presetId: 'openai-enhance',
            type: EffectType.OPENAI_IMAGE_EDIT,
            quality: OpenAIQuality.AUTO,
            background: OpenAIBackground.AUTO,
            size: OpenAISize.AUTO,
            prompt: 'Enhance this image to make it more visually appealing',
            model: OpenAIModel.GPT_IMAGE_1_5
        }
    },
    {
        id: 'gemini-oil-painting',
        label: 'Oil Painting ',
        caption: 'Transform your image into a Oil Painting-style masterpiece using Gemini AI',
        category: PresetCategory.STYLE_MODIFIERS,
        tags: [PresetTag.GEMINI],
        thumbnail: require('@/assets/design-studio/image-effects/thumbnails/default-filter-effect-oil-painting.webp'),
        isFree: false,
        order: 34,
        effectConfig: {
            presetId: 'gemini-oil-painting',
            type: EffectType.GEMINI_IMAGE_EDIT,
            prompt: 'Recreate this image in a classic oil painting style',
            imageSize: GeminiImageSizeType['1K'],
            aspectRatio: GeminiAspectRatioType['1:1'],
            model: GeminiImageModelsType.GEMINI_2_5_FLASH_IMAGE
        }
    },
    {
        id: 'gemini-renaissance-painting',
        label: 'Renaissance Painting',
        caption: 'Transform your image into a Renaissance-style masterpiece using Gemini AI',
        category: PresetCategory.STYLE_MODIFIERS,
        tags: [PresetTag.GEMINI],
        thumbnail: require('@/assets/design-studio/image-effects/thumbnails/default-filter-effect-renaissance-painting.webp'),
        isFree: false,
        order: 35,
        effectConfig: {
            presetId: 'gemini-renaissance-painting',
            type: EffectType.GEMINI_IMAGE_EDIT,
            prompt: 'Recreate this image in a renaissance painting style',
            imageSize: GeminiImageSizeType['1K'],
            aspectRatio: GeminiAspectRatioType['1:1'],
            model: GeminiImageModelsType.GEMINI_2_5_FLASH_IMAGE
        }
    },
    {
        id: 'gemini-whimsical-anime',
        label: 'Whimsical Anime',
        caption: 'Transform your image into a Whimsical Anime-style masterpiece using Gemini AI',
        category: PresetCategory.STYLE_MODIFIERS,
        tags: [PresetTag.GEMINI],
        thumbnail: require('@/assets/design-studio/image-effects/thumbnails/default-filter-effect-whimsical-anime.webp'),
        isFree: false,
        order: 36,
        effectConfig: {
            presetId: 'gemini-whimsical-anime',
            type: EffectType.GEMINI_IMAGE_EDIT,
            prompt:
                'Recreate this image as a hand-painted 2D animation frame, with soft pastel colors, whimsical fantasy scenery, detailed backgrounds and gentle, expressive characters',
            imageSize: GeminiImageSizeType['1K'],
            aspectRatio: GeminiAspectRatioType['1:1'],
            model: GeminiImageModelsType.GEMINI_2_5_FLASH_IMAGE
        }
    },
    {
        id: 'gemini-playful-cartoon',
        label: 'Playful Cartoon',
        caption: 'Transform your image into a Playful Cartoon-style masterpiece using Gemini AI',
        category: PresetCategory.STYLE_MODIFIERS,
        tags: [PresetTag.GEMINI],
        thumbnail: require('@/assets/design-studio/image-effects/thumbnails/default-filter-effect-playful-cartoon.webp'),
        isFree: false,
        order: 37,
        effectConfig: {
            presetId: 'gemini-playful-cartoon',
            type: EffectType.GEMINI_IMAGE_EDIT,
            prompt:
                'Recreate this image as a stylized 3D animated movie still, with smooth rounded shapes, big expressive eyes, soft cinematic lighting and a bright, family-friendly color palette',
            imageSize: GeminiImageSizeType['1K'],
            aspectRatio: GeminiAspectRatioType['1:1'],
            model: GeminiImageModelsType.GEMINI_2_5_FLASH_IMAGE
        }
    },
    {
        id: 'gemini-anime',
        label: 'Anime',
        caption: 'Transform your image into a Anime-style masterpiece using Gemini AI',
        category: PresetCategory.STYLE_MODIFIERS,
        tags: [PresetTag.GEMINI],
        thumbnail: require('@/assets/design-studio/image-effects/thumbnails/default-filter-effect-anime.webp'),
        isFree: false,
        order: 38,
        effectConfig: {
            presetId: 'gemini-anime',
            type: EffectType.GEMINI_IMAGE_EDIT,
            prompt: 'Recreate this image in a anime style',
            imageSize: GeminiImageSizeType['1K'],
            aspectRatio: GeminiAspectRatioType['1:1'],
            model: GeminiImageModelsType.GEMINI_2_5_FLASH_IMAGE
        }
    },
    {
        id: 'gemini-cartoon',
        label: 'Cartoon',
        caption: 'Transform your image into a Cartoon-style masterpiece using Gemini AI',
        category: PresetCategory.STYLE_MODIFIERS,
        tags: [PresetTag.GEMINI],
        thumbnail: require('@/assets/design-studio/image-effects/thumbnails/default-filter-effect-cartoon.webp'),
        isFree: false,
        order: 39,
        effectConfig: {
            presetId: 'gemini-cartoon',
            type: EffectType.GEMINI_IMAGE_EDIT,
            prompt: 'Recreate this image in a cartoon style',
            imageSize: GeminiImageSizeType['1K'],
            aspectRatio: GeminiAspectRatioType['1:1'],
            model: GeminiImageModelsType.GEMINI_2_5_FLASH_IMAGE
        }
    },
    {
        id: 'gemini-cyberpunk',
        label: 'Cyberpunk',
        caption: 'Transform your image into a Cyberpunk-style masterpiece using Gemini AI',
        category: PresetCategory.STYLE_MODIFIERS,
        tags: [PresetTag.GEMINI],
        thumbnail: require('@/assets/design-studio/image-effects/thumbnails/default-filter-effect-cyberpunk.jpg'),
        isFree: false,
        order: 40,
        effectConfig: {
            presetId: 'gemini-cyberpunk',
            type: EffectType.GEMINI_IMAGE_EDIT,
            prompt:
                'Recreate this image in a blade-runner inspired cyberpunk style, respect the original pictures proportions, surreal, clean manga style line art, cyberpunk background, retro, high-tech yet decayed atmosphere, neon-soaked shadows, reflective surfaces, contrasting bright and dark areas, tattered yet futuristic clothing, Blade Runner-inspired ambiance, clean line art',
            imageSize: GeminiImageSizeType['1K'],
            aspectRatio: GeminiAspectRatioType['1:1'],
            model: GeminiImageModelsType.GEMINI_2_5_FLASH_IMAGE
        }
    },
    {
        id: 'gemini-retro-cartoon',
        label: 'Retro Cartoon',
        caption: 'Transform your image into a Retro Cartoon-style masterpiece using Gemini AI',
        category: PresetCategory.STYLE_MODIFIERS,
        tags: [PresetTag.GEMINI],
        thumbnail: require('@/assets/design-studio/image-effects/thumbnails/default-filter-effect-retro-cartoon.webp'),
        isFree: false,
        order: 41,
        effectConfig: {
            presetId: 'gemini-retro-cartoon',
            type: EffectType.GEMINI_IMAGE_EDIT,
            prompt:
                'Recreate this image in a vintage 2D family-friendly cartoon style, with clean bold outlines, flat pastel colors, soft paper texture and rounded, expressive characters',
            imageSize: GeminiImageSizeType['1K'],
            aspectRatio: GeminiAspectRatioType['1:1'],
            model: GeminiImageModelsType.GEMINI_2_5_FLASH_IMAGE
        }
    },
    {
        id: 'gemini-bw-comic',
        label: 'B&W Comic',
        caption: 'Transform your image into a B&W Comic-style masterpiece using Gemini AI',
        category: PresetCategory.STYLE_MODIFIERS,
        tags: [PresetTag.GEMINI],
        thumbnail: require('@/assets/design-studio/image-effects/thumbnails/default-filter-effect-bw-comic.jpg'),
        isFree: false,
        order: 42,
        effectConfig: {
            presetId: 'gemini-bw-comic',
            type: EffectType.GEMINI_IMAGE_EDIT,
            prompt:
                'Recreate this image as an early 1930s black-and-white cartoon, with rubber-hose style limbs, high contrast shading, light film grain and a classic retro animation look',
            imageSize: GeminiImageSizeType['1K'],
            aspectRatio: GeminiAspectRatioType['1:1'],
            model: GeminiImageModelsType.GEMINI_2_5_FLASH_IMAGE
        }
    },
    {
        id: 'gemini-kawaii-cartoon',
        label: 'Kawaii Cartoon',
        caption: 'Transform your image into a Kawaii Cartoon-style masterpiece using Gemini AI',
        category: PresetCategory.STYLE_MODIFIERS,
        tags: [PresetTag.GEMINI],
        thumbnail: require('@/assets/design-studio/image-effects/thumbnails/default-filter-effect-kawaii-cartoon.jpg'),
        isFree: false,
        order: 43,
        effectConfig: {
            presetId: 'gemini-kawaii-cartoon',
            type: EffectType.GEMINI_IMAGE_EDIT,
            prompt: 'Recreate this image in a kawaii cartoon style',
            imageSize: GeminiImageSizeType['1K'],
            aspectRatio: GeminiAspectRatioType['1:1'],
            model: GeminiImageModelsType.GEMINI_2_5_FLASH_IMAGE
        }
    },
    {
        id: 'gemini-yellowtoon',
        label: 'Yellowtoon',
        caption: 'Transform your image into a Yellowtoon-style masterpiece using Gemini AI',
        category: PresetCategory.STYLE_MODIFIERS,
        tags: [PresetTag.GEMINI],
        thumbnail: require('@/assets/design-studio/image-effects/thumbnails/default-filter-effect-yellowtoon.webp'),
        isFree: false,
        order: 44,
        effectConfig: {
            presetId: 'gemini-yellowtoon',
            type: EffectType.GEMINI_IMAGE_EDIT,
            prompt:
                'Recreate this image in a 1990s prime-time TV cartoon style, with flat bright colors, thick black outlines, simple three-quarter views, yellow-tinted skin tones, large round eyes with dot pupils, four-fingered hands and exaggerated mouth expressions',
            imageSize: GeminiImageSizeType['1K'],
            aspectRatio: GeminiAspectRatioType['1:1'],
            model: GeminiImageModelsType.GEMINI_2_5_FLASH_IMAGE
        }
    },
    {
        id: 'gemini-pixel-art',
        label: 'Pixel Art',
        caption: 'Transform your image into a Pixel Art-style masterpiece using Gemini AI',
        category: PresetCategory.STYLE_MODIFIERS,
        tags: [PresetTag.GEMINI],
        thumbnail: require('@/assets/design-studio/image-effects/thumbnails/default-filter-effect-pixel-art.webp'),
        isFree: false,
        order: 45,
        effectConfig: {
            presetId: 'gemini-pixel-art',
            type: EffectType.GEMINI_IMAGE_EDIT,
            prompt: 'Recreate this image in a pixel art style',
            imageSize: GeminiImageSizeType['1K'],
            aspectRatio: GeminiAspectRatioType['1:1'],
            model: GeminiImageModelsType.GEMINI_2_5_FLASH_IMAGE
        }
    },
    {
        id: 'gemini-cute-retro-cartoon',
        label: 'Cute Retro Cartoon',
        caption: 'Transform your image into a Cute Retro Cartoon-style masterpiece using Gemini AI',
        category: PresetCategory.STYLE_MODIFIERS,
        tags: [PresetTag.GEMINI],
        thumbnail: require('@/assets/design-studio/image-effects/thumbnails/default-filter-effect-cute-retro-cartoon.webp'),
        isFree: false,
        order: 46,
        effectConfig: {
            presetId: 'gemini-cute-retro-cartoon',
            type: EffectType.GEMINI_IMAGE_EDIT,
            prompt:
                'Recreate this image in a minimalist mid-20th-century newspaper comic strip style, with thin clean line art, oversized round heads, small dot eyes, tiny noses, simple mouths, flat muted pastel colors and a cozy, nostalgic feel',
            imageSize: GeminiImageSizeType['1K'],
            aspectRatio: GeminiAspectRatioType['1:1'],
            model: GeminiImageModelsType.GEMINI_2_5_FLASH_IMAGE
        }
    },
    {
        id: 'gemini-3d-art',
        label: '3D Art',
        caption: 'Transform your image into a 3D Art-style masterpiece using Gemini AI',
        category: PresetCategory.STYLE_MODIFIERS,
        tags: [PresetTag.GEMINI],
        thumbnail: require('@/assets/design-studio/image-effects/thumbnails/default-filter-effect-3d-art.webp'),
        isFree: false,
        order: 47,
        effectConfig: {
            presetId: 'gemini-3d-art',
            type: EffectType.GEMINI_IMAGE_EDIT,
            prompt: 'Recreate this image in a 3d art style',
            imageSize: GeminiImageSizeType['1K'],
            aspectRatio: GeminiAspectRatioType['1:1'],
            model: GeminiImageModelsType.GEMINI_2_5_FLASH_IMAGE
        }
    },
    {
        id: 'gemini-enhance',
        label: 'Enhance Photo',
        caption: 'Enhance your photo using Gemini AI',
        category: PresetCategory.TOOLS,
        tags: [PresetTag.GEMINI],
        thumbnail: require('@/assets/design-studio/image-effects/thumbnails/default-filter-effect-enhance.webp'),
        isFree: false,
        order: 48,
        effectConfig: {
            presetId: 'gemini-enhance',
            type: EffectType.GEMINI_IMAGE_EDIT,
            prompt: 'Enhance this image to make it more visually appealing',
            imageSize: GeminiImageSizeType['1K'],
            aspectRatio: GeminiAspectRatioType['1:1'],
            model: GeminiImageModelsType.GEMINI_2_5_FLASH_IMAGE
        }
    },
    {
        id: 'gemini-openrouter-oil-painting',
        label: 'Oil Painting ',
        caption: 'Transform your image into a Oil Painting-style masterpiece using Gemini AI (OpenRouter)',
        category: PresetCategory.STYLE_MODIFIERS,
        tags: [PresetTag.OPENROUTER],
        thumbnail: require('@/assets/design-studio/image-effects/thumbnails/default-filter-effect-oil-painting.webp'),
        isFree: false,
        order: 49,
        effectConfig: {
            presetId: 'gemini-openrouter-oil-painting',
            type: EffectType.GEMINI_IMAGE_EDIT_OPENROUTER,
            prompt: 'Recreate this image in a classic oil painting style'
        }
    },
    {
        id: 'gemini-openrouter-renaissance-painting',
        label: 'Renaissance Painting',
        caption: 'Transform your image into a Renaissance-style masterpiece using Gemini AI (OpenRouter)',
        category: PresetCategory.STYLE_MODIFIERS,
        tags: [PresetTag.OPENROUTER],
        thumbnail: require('@/assets/design-studio/image-effects/thumbnails/default-filter-effect-renaissance-painting.webp'),
        isFree: false,
        order: 50,
        effectConfig: {
            presetId: 'gemini-openrouter-renaissance-painting',
            type: EffectType.GEMINI_IMAGE_EDIT_OPENROUTER,
            prompt: 'Recreate this image in a renaissance painting style'
        }
    },
    {
        id: 'gemini-openrouter-whimsical-anime',
        label: 'Whimsical Anime',
        caption: 'Transform your image into a Whimsical Anime-style masterpiece using Gemini AI (OpenRouter)',
        category: PresetCategory.STYLE_MODIFIERS,
        tags: [PresetTag.OPENROUTER],
        thumbnail: require('@/assets/design-studio/image-effects/thumbnails/default-filter-effect-whimsical-anime.webp'),
        isFree: false,
        order: 51,
        effectConfig: {
            presetId: 'gemini-openrouter-whimsical-anime',
            type: EffectType.GEMINI_IMAGE_EDIT_OPENROUTER,
            prompt:
                'Recreate this image as a hand-painted 2D animation frame, with soft pastel colors, whimsical fantasy scenery, detailed backgrounds and gentle, expressive characters'
        }
    },
    {
        id: 'gemini-openrouter-playful-cartoon',
        label: 'Playful Cartoon',
        caption: 'Transform your image into a Playful Cartoon-style masterpiece using Gemini AI (OpenRouter)',
        category: PresetCategory.STYLE_MODIFIERS,
        tags: [PresetTag.OPENROUTER],
        thumbnail: require('@/assets/design-studio/image-effects/thumbnails/default-filter-effect-playful-cartoon.webp'),
        isFree: false,
        order: 52,
        effectConfig: {
            presetId: 'gemini-openrouter-playful-cartoon',
            type: EffectType.GEMINI_IMAGE_EDIT_OPENROUTER,
            prompt:
                'Recreate this image as a stylized 3D animated movie still, with smooth rounded shapes, big expressive eyes, soft cinematic lighting and a bright, family-friendly color palette'
        }
    },
    {
        id: 'gemini-openrouter-anime',
        label: 'Anime',
        caption: 'Transform your image into a Anime-style masterpiece using Gemini AI (OpenRouter)',
        category: PresetCategory.STYLE_MODIFIERS,
        tags: [PresetTag.OPENROUTER],
        thumbnail: require('@/assets/design-studio/image-effects/thumbnails/default-filter-effect-anime.webp'),
        isFree: false,
        order: 53,
        effectConfig: {
            presetId: 'gemini-openrouter-anime',
            type: EffectType.GEMINI_IMAGE_EDIT_OPENROUTER,
            prompt: 'Recreate this image in a anime style'
        }
    },
    {
        id: 'gemini-openrouter-cartoon',
        label: 'Cartoon',
        caption: 'Transform your image into a Cartoon-style masterpiece using Gemini AI (OpenRouter)',
        category: PresetCategory.STYLE_MODIFIERS,
        tags: [PresetTag.OPENROUTER],
        thumbnail: require('@/assets/design-studio/image-effects/thumbnails/default-filter-effect-cartoon.webp'),
        isFree: false,
        order: 54,
        effectConfig: {
            presetId: 'gemini-openrouter-cartoon',
            type: EffectType.GEMINI_IMAGE_EDIT_OPENROUTER,
            prompt: 'Recreate this image in a cartoon style'
        }
    },
    {
        id: 'gemini-openrouter-cyberpunk',
        label: 'Cyberpunk',
        caption: 'Transform your image into a Cyberpunk-style masterpiece using Gemini AI (OpenRouter)',
        category: PresetCategory.STYLE_MODIFIERS,
        tags: [PresetTag.OPENROUTER],
        thumbnail: require('@/assets/design-studio/image-effects/thumbnails/default-filter-effect-cyberpunk.jpg'),
        isFree: false,
        order: 55,
        effectConfig: {
            presetId: 'gemini-openrouter-cyberpunk',
            type: EffectType.GEMINI_IMAGE_EDIT_OPENROUTER,
            prompt:
                'Recreate this image in a blade-runner inspired cyberpunk style, respect the original pictures proportions, surreal, clean manga style line art, cyberpunk background, retro, high-tech yet decayed atmosphere, neon-soaked shadows, reflective surfaces, contrasting bright and dark areas, tattered yet futuristic clothing, Blade Runner-inspired ambiance, clean line art'
        }
    },
    {
        id: 'gemini-openrouter-retro-cartoon',
        label: 'Retro Cartoon',
        caption: 'Transform your image into a Retro Cartoon-style masterpiece using Gemini AI (OpenRouter)',
        category: PresetCategory.STYLE_MODIFIERS,
        tags: [PresetTag.OPENROUTER],
        thumbnail: require('@/assets/design-studio/image-effects/thumbnails/default-filter-effect-retro-cartoon.webp'),
        isFree: false,
        order: 56,
        effectConfig: {
            presetId: 'gemini-openrouter-retro-cartoon',
            type: EffectType.GEMINI_IMAGE_EDIT_OPENROUTER,
            prompt:
                'Recreate this image in a vintage 2D family-friendly cartoon style, with clean bold outlines, flat pastel colors, soft paper texture and rounded, expressive characters'
        }
    },
    {
        id: 'gemini-openrouter-bw-comic',
        label: 'B&W Comic',
        caption: 'Transform your image into a B&W Comic-style masterpiece using Gemini AI (OpenRouter)',
        category: PresetCategory.STYLE_MODIFIERS,
        tags: [PresetTag.OPENROUTER],
        thumbnail: require('@/assets/design-studio/image-effects/thumbnails/default-filter-effect-bw-comic.jpg'),
        isFree: false,
        order: 57,
        effectConfig: {
            presetId: 'gemini-openrouter-bw-comic',
            type: EffectType.GEMINI_IMAGE_EDIT_OPENROUTER,
            prompt:
                'Recreate this image as an early 1930s black-and-white cartoon, with rubber-hose style limbs, high contrast shading, light film grain and a classic retro animation look'
        }
    },
    {
        id: 'gemini-openrouter-kawaii-cartoon',
        label: 'Kawaii Cartoon',
        caption: 'Transform your image into a Kawaii Cartoon-style masterpiece using Gemini AI (OpenRouter)',
        category: PresetCategory.STYLE_MODIFIERS,
        tags: [PresetTag.OPENROUTER],
        thumbnail: require('@/assets/design-studio/image-effects/thumbnails/default-filter-effect-kawaii-cartoon.jpg'),
        isFree: false,
        order: 58,
        effectConfig: {
            presetId: 'gemini-openrouter-kawaii-cartoon',
            type: EffectType.GEMINI_IMAGE_EDIT_OPENROUTER,
            prompt: 'Recreate this image in a kawaii cartoon style'
        }
    },
    {
        id: 'gemini-openrouter-yellowtoon',
        label: 'Yellowtoon',
        caption: 'Transform your image into a Yellowtoon-style masterpiece using Gemini AI (OpenRouter)',
        category: PresetCategory.STYLE_MODIFIERS,
        tags: [PresetTag.OPENROUTER],
        thumbnail: require('@/assets/design-studio/image-effects/thumbnails/default-filter-effect-yellowtoon.webp'),
        isFree: false,
        order: 59,
        effectConfig: {
            presetId: 'gemini-openrouter-yellowtoon',
            type: EffectType.GEMINI_IMAGE_EDIT_OPENROUTER,
            prompt:
                'Recreate this image in a 1990s prime-time TV cartoon style, with flat bright colors, thick black outlines, simple three-quarter views, yellow-tinted skin tones, large round eyes with dot pupils, four-fingered hands and exaggerated mouth expressions'
        }
    },
    {
        id: 'gemini-openrouter-pixel-art',
        label: 'Pixel Art',
        caption: 'Transform your image into a Pixel Art-style masterpiece using Gemini AI (OpenRouter)',
        category: PresetCategory.STYLE_MODIFIERS,
        tags: [PresetTag.OPENROUTER],
        thumbnail: require('@/assets/design-studio/image-effects/thumbnails/default-filter-effect-pixel-art.webp'),
        isFree: false,
        order: 60,
        effectConfig: {
            presetId: 'gemini-openrouter-pixel-art',
            type: EffectType.GEMINI_IMAGE_EDIT_OPENROUTER,
            prompt: 'Recreate this image in a pixel art style'
        }
    },
    {
        id: 'gemini-openrouter-cute-retro-cartoon',
        label: 'Cute Retro Cartoon',
        caption: 'Transform your image into a Cute Retro Cartoon-style masterpiece using Gemini AI (OpenRouter)',
        category: PresetCategory.STYLE_MODIFIERS,
        tags: [PresetTag.OPENROUTER],
        thumbnail: require('@/assets/design-studio/image-effects/thumbnails/default-filter-effect-cute-retro-cartoon.webp'),
        isFree: false,
        order: 61,
        effectConfig: {
            presetId: 'gemini-openrouter-cute-retro-cartoon',
            type: EffectType.GEMINI_IMAGE_EDIT_OPENROUTER,
            prompt:
                'Recreate this image in a minimalist mid-20th-century newspaper comic strip style, with thin clean line art, oversized round heads, small dot eyes, tiny noses, simple mouths, flat muted pastel colors and a cozy, nostalgic feel'
        }
    },
    {
        id: 'gemini-openrouter-3d-art',
        label: '3D Art',
        caption: 'Transform your image into a 3D Art-style masterpiece using Gemini AI (OpenRouter)',
        category: PresetCategory.STYLE_MODIFIERS,
        tags: [PresetTag.OPENROUTER],
        thumbnail: require('@/assets/design-studio/image-effects/thumbnails/default-filter-effect-3d-art.webp'),
        isFree: false,
        order: 62,
        effectConfig: {
            presetId: 'gemini-openrouter-3d-art',
            type: EffectType.GEMINI_IMAGE_EDIT_OPENROUTER,
            prompt: 'Recreate this image in a 3d art style '
        }
    },
    {
        id: 'gemini-openrouter-enhance',
        label: 'Enhance Photo',
        caption: 'Enhance your photo using Gemini AI (OpenRouter)',
        category: PresetCategory.TOOLS,
        tags: [PresetTag.OPENROUTER],
        thumbnail: require('@/assets/design-studio/image-effects/thumbnails/default-filter-effect-enhance.webp'),
        isFree: false,
        order: 63,
        effectConfig: {
            presetId: 'gemini-openrouter-enhance',
            type: EffectType.GEMINI_IMAGE_EDIT_OPENROUTER,
            prompt: 'Enhance this image to make it more visually appealing'
        }
    },
    {
        id: 'gemini-openrouter-custom-prompt',
        label: 'Custom Prompt',
        caption: "Transform your shopper's images using a custom prompt, fit to your needs.",
        category: PresetCategory.CUSTOM_PROMPTS,
        tags: [PresetTag.OPENROUTER],
        thumbnail: require('@/assets/design-studio/image-effects/thumbnails/custom-prompt.png'),
        isFree: false,
        order: 64,
        effectConfig: {
            presetId: 'gemini-openrouter-custom-prompt',
            type: EffectType.GEMINI_IMAGE_EDIT_OPENROUTER,
            prompt: 'Transform this image with a custom prompt'
        }
    },
    {
        id: 'gemini-custom-prompt',
        label: 'Custom Prompt',
        caption: 'Transform your image with a custom prompt using Gemini AI',
        category: PresetCategory.CUSTOM_PROMPTS,
        tags: [PresetTag.GEMINI],
        thumbnail: require('@/assets/design-studio/image-effects/thumbnails/gemini-filter-effect-custom-prompt.png'),
        isFree: false,
        order: 65,
        effectConfig: {
            presetId: 'gemini-custom-prompt',
            type: EffectType.GEMINI_IMAGE_EDIT,
            prompt: 'Transform this image with a custom prompt',
            imageSize: GeminiImageSizeType['1K'],
            aspectRatio: GeminiAspectRatioType['1:1'],
            model: GeminiImageModelsType.GEMINI_2_5_FLASH_IMAGE
        }
    },
    {
        id: 'openai-custom-prompt',
        label: 'Custom Prompt',
        caption: 'Transform your image with a custom prompt using OpenAI',
        category: PresetCategory.CUSTOM_PROMPTS,
        tags: [PresetTag.OPENAI],
        thumbnail: require('@/assets/design-studio/image-effects/thumbnails/openai-filter-effect-custom-prompt.png'),
        isFree: false,
        order: 66,
        effectConfig: {
            presetId: 'openai-custom-prompt',
            type: EffectType.OPENAI_IMAGE_EDIT,
            quality: OpenAIQuality.AUTO,
            background: OpenAIBackground.AUTO,
            size: OpenAISize.AUTO,
            prompt: 'Transform this image with a custom prompt',
            model: OpenAIModel.GPT_IMAGE_1_5
        }
    },
    {
        id: 'openai-openrouter-custom-prompt',
        label: 'Custom Prompt',
        caption: 'Transform your image with a custom prompt using OpenAI (OpenRouter)',
        category: PresetCategory.CUSTOM_PROMPTS,
        tags: [PresetTag.OPENROUTER],
        thumbnail: require('@/assets/design-studio/image-effects/thumbnails/openai-filter-effect-custom-prompt.png'),
        isFree: false,
        order: 67,
        effectConfig: {
            presetId: 'openai-openrouter-custom-prompt',
            type: EffectType.OPENAI_IMAGE_EDIT_OPENROUTER,
            prompt: 'Transform this image with a custom prompt',
            isMini: true
        }
    },
    {
        id: 'picwish-upscale',
        label: 'Enhance Photo',
        caption: 'Enhance your image quality using PicWish',
        category: PresetCategory.TOOLS,
        tags: [PresetTag.PICWISH],
        thumbnail: require('@/assets/design-studio/image-effects/thumbnails/default-filter-effect-enhance.webp'),
        isFree: false,
        order: 68,
        effectConfig: {
            presetId: 'picwish-upscale',
            type: EffectType.PICWISH_UPSCALE,
            scaleFactor: PicwishUpScaleFactor.AUTO,
            enhancementType: PicwishEnhancementType.CLEAN
        }
    }
];

export const getPresetsByCategory = (category: PresetCategory): ImageEffectPreset[] => {
    return IMAGE_EFFECT_PRESETS.filter((preset) => preset.category === category).sort(
        (a, b) => (a.order || 0) - (b.order || 0)
    );
};

export const applyPresetFilters = ({
    searchQuery,
    effectiveCategory,
    selectedTags,
    filterBySelectedTags
}: {
    searchQuery?: string;
    effectiveCategory?: EffectiveCategory;
    selectedTags?: PresetTag[];
    filterBySelectedTags?: boolean;
}): ImageEffectPreset[] => {
    let presets = IMAGE_EFFECT_PRESETS;

    if (effectiveCategory === SPECIAL_CATEGORY_VALUES.FREE) {
        presets = presets.filter((p) => p.isFree);
    } else if (effectiveCategory) {
        presets = getPresetsByCategory(effectiveCategory);
    }

    if (filterBySelectedTags && searchQuery?.trim()) {
        const searchTerm = searchQuery.toLowerCase().trim();
        presets = presets.filter(
            (preset) =>
                preset.label.toLowerCase().includes(searchTerm) ||
                preset.caption?.toLowerCase().includes(searchTerm) ||
                preset.tags.some((tag) => tag.toLowerCase().includes(searchTerm))
        );
    }

    if (filterBySelectedTags && selectedTags && selectedTags.length > 0) {
        presets = presets.filter((preset) => selectedTags.some((tag) => preset.tags.includes(tag)));
    }

    return presets;
};
